/**
 * A small typed fetch wrapper.
 *
 * Deliberately not a port of unified-cloud's shared/utils/httpClient. Most of that file exists to cope
 * with a cross-origin API: injecting global headers only when same-origin can be proven, deriving a
 * per-project regional base URL, navigating on certain statuses. None of it applies here, because the
 * Worker serves this SPA and /api from one origin — every request is same-origin and relative.
 *
 * What is kept is the part that earns its place: distinguishable error types, so a caller can tell "not
 * found" and "not permitted" apart from a real failure.
 */

export class HttpNotFoundError extends Error {
    public constructor(url: string) {
        super(`Request to ${url} returned 404`);
        this.name = 'HttpNotFoundError';
    }
}

export class HttpForbiddenError extends Error {
    public constructor(
        url: string,
        public readonly detail: string | null,
    ) {
        super(detail ?? `Request to ${url} returned 403`);
        this.name = 'HttpForbiddenError';
    }
}

export class HttpUnauthorizedError extends Error {
    /**
     * Carries the server's reason. A 401 here has several distinct causes — no identity header at all,
     * a signature that would not verify, an address outside the permitted domain — and the API says
     * which in the response body.
     */
    public constructor(
        url: string,
        public readonly detail: string | null,
    ) {
        super(detail ?? `Request to ${url} returned 401`);
        this.name = 'HttpUnauthorizedError';
    }
}

/**
 * Any other non-OK answer: a 500, a 502 from the balancer, anything without a dedicated class.
 *
 * Typed so describeError can tell "the server said something" (detail, worth showing verbatim) from "the
 * request just failed with a status" — which used to become a plain Error whose message named the URL and
 * status, the exact string that helper exists to keep away from readers.
 */
export class HttpRequestFailedError extends Error {
    public constructor(
        url: string,
        public readonly status: number,
        public readonly detail: string | null,
    ) {
        super(detail ?? `Request to ${url} failed (${status})`);
        this.name = 'HttpRequestFailedError';
    }
}

/** The message the API put in the body, when it put one there. */
const detailFrom = async (response: Response): Promise<string | null> => {
    try {
        const body = (await response.json()) as {message?: unknown};

        return typeof body.message === 'string' ? body.message : null;
    } catch {
        return null;
    }
};

const errorFor = async (response: Response, url: string): Promise<Error> => {
    if (response.status === 404) return new HttpNotFoundError(url);
    if (response.status === 401) return new HttpUnauthorizedError(url, await detailFrom(response));
    if (response.status === 403) return new HttpForbiddenError(url, await detailFrom(response));

    return new HttpRequestFailedError(url, response.status, await detailFrom(response));
};

const request = async <T>(url: string, init?: RequestInit): Promise<T> => {
    const response = await fetch(url, {
        ...init,
        headers: {accept: 'application/json', ...init?.headers},
    });

    if (!response.ok) throw await errorFor(response, url);

    if (response.status === 204) return undefined as T;

    /*
     * Read as text first: a handler that returns `null` can answer a 200 with an EMPTY body,
     * not the four characters `null`, and `response.json()` throws on an empty body. That turned
     * "you have no check-in today" into a failed request. An empty body is null.
     */
    const text = await response.text();

    return text === '' ? (null as T) : (JSON.parse(text) as T);
};

export const getRequest = <T>(url: string): Promise<T> => request<T>(url);

export const postRequest = <T>(url: string, body?: unknown): Promise<T> =>
    request<T>(url, {
        method: 'POST',
        headers: body === undefined ? {} : {'content-type': 'application/json'},
        body: body === undefined ? undefined : JSON.stringify(body),
    });

/**
 * PUT: the resource's full new state, which is what a check-in save is. `keepalive` lets it
 * outlive the page — for a save sent as the tab closes; the browser caps such a body at 64KB.
 */
export const putRequest = <T>(
    url: string,
    body: unknown,
    {keepalive = false}: {keepalive?: boolean} = {},
): Promise<T> =>
    request<T>(url, {
        method: 'PUT',
        headers: {'content-type': 'application/json'},
        body: JSON.stringify(body),
        keepalive,
    });

export const patchRequest = <T>(url: string, body: unknown): Promise<T> =>
    request<T>(url, {
        method: 'PATCH',
        headers: {'content-type': 'application/json'},
        body: JSON.stringify(body),
    });

/** DELETE answers 204, which `request` turns into undefined rather than trying to parse a body. */
export const deleteRequest = (url: string): Promise<void> => request<void>(url, {method: 'DELETE'});
